import React, { useEffect, useRef, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  useMapEvents,
} from "react-leaflet";
import NewMapPopup from "./NewMapPopup";
import MapHandler from "../mapComponents/MapHandler";
import { getClickedLocationIcon } from "../mapComponents/MapIcon";
import SearchControl from "../mapComponents/SearchControl";

const LocationUpdater = ({ position, zoom, offset }) => {
  const map = useMap();

  useEffect(() => {
    if (position) {
      // Adjust position with offset to center the marker properly
      const point = map.project(position, map.getZoom()).subtract(offset);
      const adjustedPosition = map.unproject(point, map.getZoom());

      map.flyTo(adjustedPosition, zoom, {
        duration: 1,
        easeLinearity: 0.25,
      });
    }
  }, [position, zoom, map, offset]);

  return null;
};

const OSNewMapContainer = ({ searchLocation, id, handleFormData }) => {
  const defaultCenter = [20, 80];
  const defaultZoom = 13;

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const searchMarkerRef = useRef(null);
  const selectedMarkerRef = useRef(null);
  const mapRef = useRef(null);

  const MapClickHandler = () => {
    useMapEvents({
      click: (e) => {
        const { lat, lng } = e.latlng;
        setSelectedLocation({
          lat,
          lng,
        });
      },
    });
    return null;
  };

  useEffect(() => {
    if (searchLocation && searchMarkerRef.current) {
      searchMarkerRef.current.openPopup();
    }
    if (selectedLocation && selectedMarkerRef.current) {
      setTimeout(() => {
        selectedMarkerRef.current.openPopup();
      }, 100);
    }
  }, [searchLocation, selectedLocation]);

  useEffect(() => {
    if (mapRef.current) {
      setTimeout(() => {
        mapRef.current.invalidateSize?.();
      }, 200);
    }
  }, [isExpanded]);

  const handleAddressSelect = () => {
    setIsExpanded(true);
  };

  const handleFill = (data) => {
    data === true &&
      handleFormData(
        `${
          selectedLocation?.lat?.toFixed(6) || searchLocation?.lat?.toFixed(6)
        }, ${
          selectedLocation?.lng?.toFixed(6) || searchLocation?.lng?.toFixed(6)
        }`
      );
  };

  // Calculate offset for the map to move the location marker 20% right
  const mapOffset = mapRef.current?.getSize?.()
    ? mapRef.current.getSize().divideBy(2).multiplyBy([1.2, 1])
    : { x: 150, y: 25 };

  return (
    <div className="relative h-full w-full">
      <MapContainer
        ref={(ref) => {
          mapRef.current = ref?.leafletElement || null;
        }}
        center={
          selectedLocation
            ? [selectedLocation.lat, selectedLocation.lng]
            : defaultCenter
        }
        zoom={selectedLocation ? 14 : defaultZoom}
        className="h-full w-full overflow-hidden rounded-md"
        scrollWheelZoom={true}
        zoomAnimation={true}
      >
        <MapClickHandler />
        <TileLayer
          attribution="&copy;"
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapHandler />
        <LocationUpdater
          position={
            selectedLocation
              ? [selectedLocation.lat, selectedLocation.lng]
              : searchLocation
              ? [searchLocation.lat, searchLocation.lng]
              : null
          }
          zoom={15}
          offset={mapOffset}
        />

        {searchLocation && (
          <Marker
            ref={searchMarkerRef}
            position={[searchLocation.lat, searchLocation.lng]}
            icon={getClickedLocationIcon()}
          >
            <Popup
              closeButton={false}
              style={{
                transform: "translate(20%, -60%)",
              }}
            >
              <NewMapPopup
                id={id}
                location={searchLocation}
                handleFillPress={handleFill}
              />
            </Popup>
          </Marker>
        )}

        {selectedLocation && (
          <Marker
            ref={selectedMarkerRef}
            position={[selectedLocation.lat, selectedLocation.lng]}
            icon={getClickedLocationIcon()}
          >
            <Popup closeButton={false} style={{}}>
              <NewMapPopup
                id={id}
                location={selectedLocation}
                handleFillPress={handleFill}
              />
            </Popup>
          </Marker>
        )}
      </MapContainer>

      {/* Search Control */}
      <div
        className={`absolute z-[1000] transition-all duration-300 ${
          isExpanded
            ? "left-0 top-0 h-full w-[360px] bg-white shadow-lg"
            : "left-14 top-4"
        }`}
      >
        <SearchControl
          isExpanded={isExpanded}
          onAddressSelect={handleAddressSelect}
        />
      </div>
    </div>
  );
};

export default OSNewMapContainer;
